<template>
  <div class="v-overlay" :class="classes" :style="styles" @click="emit('click', $event)">
    <div class="v-overlay__scrim" :style="{ background: props.color, opacity: computedOpacity }"></div>
    <div v-if="isActive" class="v-overlay__content">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  absolute?: boolean;
  color?: string;
  opacity?: number | string;
  value?: boolean;
  zIndex?: number | string;
}

const props = withDefaults(defineProps<Props>(), {
  absolute: false,
  color: '#212121',
  opacity: 0.75,
  value: true,
  zIndex: 5,
});

const emit = defineEmits<{
  (e: 'click', value: MouseEvent): void;
}>();

const isActive = computed(() => props.value);
const computedOpacity = computed(() => Number(isActive.value ? props.opacity : 0));
const classes = computed(() => ({
  'v-overlay--absolute': props.absolute,
  'v-overlay--active': isActive.value,
}));
const styles = computed(() => ({
  zIndex: Number(props.zIndex),
}));
</script>

<style scoped lang="scss">
$overlay-transition: $primary-transition, z-index 1ms !default;

.v-overlay {
  align-items: center;
  border-radius: inherit;
  display: flex;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  transition: $overlay-transition;

  &__content {
    position: relative;
  }

  &__scrim {
    border-radius: inherit;
    bottom: 0;
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transition: inherit;
    width: 100%;
    will-change: opacity;
  }

  &--absolute {
    position: absolute;
  }

  &--active {
    pointer-events: auto;
  }
}
</style>
